import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useBatch } from '@/contexts/BatchContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Plus,
  MoreHorizontal,
  Edit,
  Trash,
  Loader2,
  Archive
} from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { batchApi } from '@/lib/api';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const BatchesPage = () => {
  const { batches, loading, error, refetchBatches } = useBatch();
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [isArchiving, setIsArchiving] = useState(false);
  const [archiveId, setArchiveId] = useState<string | null>(null);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [batchToArchive, setBatchToArchive] = useState<{id: string, year: string} | null>(null);
  const { toast } = useToast();



  const handleDeleteBatch = async (id: string) => {
    try {
      setIsDeleting(true);
      await batchApi.delete(id);
      await refetchBatches();
      toast({
        title: 'Success',
        description: 'Batch deleted successfully',
      });
    } catch (err) {
      console.error('Error deleting batch:', err);
      toast({
        title: 'Error',
        description: 'Failed to delete batch',
        variant: 'destructive',
      });
    } finally {
      setIsDeleting(false);
      setDeleteId(null);
    }
  };

  const confirmDelete = (id: string) => {
    setDeleteId(id);
  };

  const cancelDelete = () => {
    setDeleteId(null);
  };

  const handleArchiveBatch = (id: string, year: string) => {
    setBatchToArchive({ id, year });
    setArchiveDialogOpen(true);
  };

  const confirmArchiveBatch = async () => {
    if (!batchToArchive) return;

    try {
      setIsArchiving(true);
      setArchiveId(batchToArchive.id);

      await batchApi.archive(batchToArchive.id);

      toast({
        title: 'Success',
        description: `Batch ${batchToArchive.year} has been archived successfully. All associated students and classes have also been archived.`,
      });

      await refetchBatches();
    } catch (err) {
      console.error('Error archiving batch:', err);
      toast({
        title: 'Error',
        description: 'Failed to archive batch',
        variant: 'destructive',
      });
    } finally {
      setIsArchiving(false);
      setArchiveId(null);
      setArchiveDialogOpen(false);
      setBatchToArchive(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold tracking-tight">Batches</h1>
        <Button asChild>
          <Link to="/batches/new">
            <Plus className="mr-2 h-4 w-4" />
            Add Batch
          </Link>
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Manage Batches</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <div className="animate-spin rounded-full h-6 w-6 border-2 border-gray-200 border-t-blue-600 mx-auto mb-3"></div>
                <p className="text-muted-foreground text-sm">Loading batches...</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-8">
              <p className="text-destructive mb-4">{error}</p>
              <Button onClick={() => refetchBatches()}>Retry</Button>
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Batch</TableHead>
                    <TableHead>Start Date</TableHead>
                    <TableHead>End Date</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {batches.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="h-24 text-center">
                        No batches found. Create your first batch.
                      </TableCell>
                    </TableRow>
                  ) : (
                    batches.map((batch) => (
                      <TableRow key={batch.id}>
                        <TableCell className="font-medium">{batch.year}</TableCell>
                        <TableCell>{batch.startDate}</TableCell>
                        <TableCell>{batch.endDate}</TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" className="h-8 w-8 p-0">
                                <span className="sr-only">Open menu</span>
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuLabel>Actions</DropdownMenuLabel>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem asChild>
                                <Link to={`/batches/${batch.id}/edit`}>
                                  <Edit className="mr-2 h-4 w-4" />
                                  <span>Edit</span>
                                </Link>
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleArchiveBatch(batch.id, batch.year)}
                                disabled={isArchiving && archiveId === batch.id}
                              >
                                {isArchiving && archiveId === batch.id ? (
                                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                ) : (
                                  <Archive className="mr-2 h-4 w-4" />
                                )}
                                <span>Archive</span>
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => confirmDelete(batch.id)}
                                className="text-destructive focus:text-destructive"
                              >
                                <Trash className="mr-2 h-4 w-4" />
                                <span>Delete</span>
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={deleteId !== null} onOpenChange={cancelDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the batch and all associated data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteId && handleDeleteBatch(deleteId)}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Archive Confirmation Dialog */}
      <AlertDialog open={archiveDialogOpen} onOpenChange={setArchiveDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive Batch</AlertDialogTitle>
            <AlertDialogDescription>
              This will archive the batch "{batchToArchive?.year}" and all associated students and classes. Are you sure you want to continue?
              <br /><br />
              <strong>Note:</strong> Archived items can be restored later from the Archive Management page.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmArchiveBatch}
              disabled={isArchiving}
              className="bg-orange-600 text-white hover:bg-orange-700"
            >
              {isArchiving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Archiving...
                </>
              ) : (
                'Archive'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default BatchesPage;
